import { useState, useCallback, useEffect } from "react";
import { generateWecomQRCode } from "@/apis/wecom";
import type { WecomQRCodeResponse } from "@/apis/wecom";

// ==================== 类型定义 ====================
/**
 * useWecomQRCode Hook 返回值类型
 */
export interface UseWecomQRCodeReturn {
  /**
   * 企业微信参数
   */
  wecomParams: WecomQRCodeResponse | null;
  /**
   * 二维码 Canvas 引用（用于绘制二维码）
   */
  qrcodeCanvasRef: React.RefObject<HTMLCanvasElement>;
  /**
   * 加载中状态
   */
  loading: boolean;
  /**
   * 错误信息
   */
  error: string | null;
  /**
   * 剩余有效时间（毫秒）
   */
  remainingTime: number;
  /**
   * 是否即将过期
   */
  isExpiringSoon: boolean;
  /**
   * 获取企业微信参数并生成二维码
   */
  generateQRCode: () => Promise<void>;
  /**
   * 清空二维码
   */
  clearQRCode: () => void;
}

// ==================== 常量配置 ====================
/**
 * 即将过期的警告时间阈值（毫秒）
 * 5分钟
 */
const EXPIRY_WARNING_TIME = 5 * 60 * 1000;

// ==================== 自定义Hook ====================
/**
 * 企业微信二维码生成 Hook
 * 用于获取企业微信参数，前端使用这些参数通过企业微信库生成二维码
 *
 * 使用示例：
 * ```typescript
 * const { wecomParams, qrcodeCanvasRef, generateQRCode, remainingTime } = useWecomQRCode();
 *
 * // 生成二维码
 * await generateQRCode();
 *
 * // 使用企业微信库生成二维码
 * if (wecomParams) {
 *   const authUrl = `https://open.weixin.qq.com/connect/oauth2/authorize?...`;
 *   // 使用企业微信提供的 SDK 生成二维码
 * }
 * ```
 */
export const useWecomQRCode = (): UseWecomQRCodeReturn => {
  const [wecomParams, setWecomParams] = useState<WecomQRCodeResponse | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [remainingTime, setRemainingTime] = useState(0);
  const qrcodeCanvasRef = useCallback(() => null, []) as any; // TODO: 如需使用 Canvas 绘制二维码

  // ==================== 计算派生状态 ====================
  const isExpiringSoon =
    remainingTime < EXPIRY_WARNING_TIME && remainingTime > 0;

  // ==================== 获取企业微信参数 ====================
  const generateQRCode = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // 调用后端接口获取企业微信参数
      const response = await generateWecomQRCode();

      if (response.data?.data) {
        const params = response.data.data;
        setWecomParams(params);
        // 计算剩余时间
        setRemainingTime(params.expiresAt - Date.now());
      } else {
        throw new Error("获取企业微信参数失败");
      }
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : "生成企业微信二维码失败";
      setError(errorMsg);
      console.error("获取企业微信参数错误:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // ==================== 清空二维码 ====================
  const clearQRCode = useCallback(() => {
    setWecomParams(null);
    setError(null);
    setRemainingTime(0);
  }, []);

  // ==================== 定时更新剩余时间 ====================
  useEffect(() => {
    if (!wecomParams) {
      return;
    }

    const timerId = setInterval(() => {
      setRemainingTime((prev) => {
        const newTime = wecomParams.expiresAt - Date.now();

        // 时间到期时清空
        if (newTime <= 0) {
          clearQRCode();
          return 0;
        }

        return newTime;
      });
    }, 1000);

    return () => clearInterval(timerId);
  }, [wecomParams, clearQRCode]);

  return {
    wecomParams,
    qrcodeCanvasRef,
    loading,
    error,
    remainingTime,
    isExpiringSoon,
    generateQRCode,
    clearQRCode,
  };
};
